<template>
  <Modal
    v-model:visible="isVisible"
    class="order-or-shipment-template-modal"
    :header="t('common.saveParam', { param: t('templates.templates') })"
    @keyup.enter="onSave"
    @keyup.esc="isVisible = false"
  >
    <InputWrapper
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="order-or-shipment-template-name"
      :errors="v$.name.$errors"
      :label="t('common.name')"
    >
      <InputText
        :id="inputId"
        v-model="internalValue.name"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        autofocus
        :placeholder="t('common.name')"
      />
    </InputWrapper>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />
      <RygenButton
        mode="primary"
        icon="pi pi-save"
        :label="t('common.save')"
        @click="onSave"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { nextTick, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';

import { OrderOrShipmentTemplate } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: false,
});

const props = defineProps({
  name: {
    type: String,
    default: () => '',
  },
  templates: {
    type: Array as PropType<OrderOrShipmentTemplate[]>,
    default: () => [],
  },
});
const { name, templates } = toRefs(props);

const emit = defineEmits<{
  (event: 'save', name: string): void;
}>();

const { t } = useI18n();
const { uid } = useUid('save-order-template-modal');

const internalValue = ref({ name: name.value });
const rules = {
  name: { required: required(t('common.name')) },
};
const { externalResults, resetModel, v$, validate } = useValidations(
  internalValue,
  rules,
  uid,
);

const templateNameAvailable = () => {
  if (
    !templates.value.find(
      (template) => template.name === internalValue.value.name,
    )
  ) {
    return true;
  }

  externalResults.value = {
    name: [t('templates.errors.templateNameExists')],
  };
  return false;
};

const onSave = async () => {
  if ((await validate()) && templateNameAvailable()) {
    isVisible.value = false;
    emit('save', internalValue.value.name);
  }
};

watch(
  name,
  (newVal: string) => {
    internalValue.value.name = newVal;
  },
  { immediate: true },
);

watch(isVisible, (newVal: boolean) => {
  if (newVal) {
    nextTick(() => resetModel(internalValue.value));
  }
});
</script>
